import { createResource, createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import {
  fetchLiveStock,
  formatPct,
  IRRADIATION_LABELS,
  type LiveStockEntry,
  type IrradiationKind,
} from '@lib/supabase/queries';
import { fetchPortfolioStrainIds, type PortfolioSymptom } from '@lib/supabase/portfolio';
import { fetchWatchedStrainIds } from '@lib/supabase/watchlist';
import {
  fetchStrainSymptoms,
  setStrainSymptoms,
} from '@lib/supabase/strainSymptoms';
import { fetchSymptoms, suggestSymptom, type Symptom } from '@lib/supabase/symptoms';
import { fetchExcludedBrands } from '@lib/supabase/excludedBrands';
import { authStore } from '@stores/session';
import StockBadge from '@components/ui/StockBadge';
import CollapsibleSection from '@components/ui/CollapsibleSection';
import DualRangeSlider from '@components/ui/DualRangeSlider';
import PageHead from '@components/seo/PageHead';
import FilterDrawer from '@components/layout/FilterDrawer';
import MedicalStatusGate from '@components/medical/MedicalStatusGate';
import CardQuickActions from '@components/strain/CardQuickActions';
import RelationalSymptomEditor from '@components/strain/RelationalSymptomEditor';
import {
  summariseStock,
  strainNameWithThc,
  type StockSummary,
} from '@lib/strainDisplay';
import {
  urlFilters,
  urlString,
  urlBool,
  urlNumber,
  urlSet,
  urlStringSet,
} from '@lib/urlFilters';

type Lineage = 'sativa' | 'indica' | 'hybrid';
type LineageFilter = Lineage | 'unknown';
type Category = 'Flower' | 'Oil' | 'Vape' | 'Accessory';
// 'unknown' = stock row where irradiation is NULL (pharmacy didn't report it —
// typical for oils, vapes, accessories). Kept separate from the four real
// sterilisation methods so patients can explicitly opt in or out of it.
type IrradiationFilter = IrradiationKind | 'unknown';

type FilterKey =
  | 'search'
  | 'pharmacy'
  | 'inStock'
  | 'category'
  | 'lineage'
  | 'irradiation'
  | 'brand'
  | 'price'
  | 'thc'
  | 'cbd'
  | 'symptom';

const LINEAGE_OPTIONS: LineageFilter[] = ['sativa', 'indica', 'hybrid', 'unknown'];
const LINEAGE_LABELS: Record<LineageFilter, string> = {
  sativa: 'Sativa',
  indica: 'Indica',
  hybrid: 'Hybrid',
  unknown: 'Unknown',
};
const CATEGORY_OPTIONS: Category[] = ['Flower', 'Oil', 'Vape', 'Accessory'];
const IRRADIATION_FILTER_OPTIONS: IrradiationFilter[] = ['none', 'e-beam', 'beta', 'gamma', 'unknown'];
const IRRADIATION_FILTER_LABELS: Record<IrradiationFilter, string> = {
  ...IRRADIATION_LABELS,
  unknown: 'Unknown / Not reported',
};

const DEFAULT_PRICE_MAX = 20;
const DEFAULT_THC_MAX = 35;
const DEFAULT_CBD_MAX = 25;

// Validators for url-set filters. Same defensive parsing pattern as
// StrainListRoute — a malformed URL param is dropped rather than
// surfacing as a stale filter the user can't see in any sidebar
// option.
const isCategory = (v: string): v is Category =>
  (CATEGORY_OPTIONS as string[]).includes(v);
const isLineageFilter = (v: string): v is LineageFilter =>
  (LINEAGE_OPTIONS as string[]).includes(v);
const isIrradiationFilter = (v: string): v is IrradiationFilter =>
  (IRRADIATION_FILTER_OPTIONS as string[]).includes(v);

export default function StockRoute(): JSX.Element {
  const [entries] = createResource(fetchLiveStock);

  // Bulk membership for the per-card quick actions. Empty Set when
  // signed out; CardQuickActions hides itself in that case.
  const [portfolioIds] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchPortfolioStrainIds() : new Set<string>()),
  );
  const [watchedIds] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchWatchedStrainIds() : new Set<string>()),
  );

  // Brands hidden in Settings — excluded from results by default with a
  // one-tap "show anyway" override.
  const [excludedBrands] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchExcludedBrands() : new Set<string>()),
  );
  const [showExcluded, setShowExcluded] = createSignal(false);

  // Symptom tagging (signed-in only). The catalog feeds the picker; the
  // per-strain map (keyed by strain_id) drives each card's chips and the
  // Symptoms filter. Both mutate locally on edit so chips update without
  // a refetch flashing the Suspense fallback. Empty when signed out.
  const [symptomCatalog, { mutate: mutateCatalog }] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchSymptoms() : []),
  );
  const [strainSymptoms, { mutate: mutateStrainSymptoms }] = createResource(
    () => authStore.user()?.id ?? null,
    async (id) => (id ? fetchStrainSymptoms() : new Map<string, PortfolioSymptom[]>()),
  );
  const symptomsFor = (strainId: string): PortfolioSymptom[] =>
    strainSymptoms()?.get(strainId) ?? [];

  function symptomsFromIds(ids: string[], extra?: Symptom[]): PortfolioSymptom[] {
    const catalog = symptomCatalog() ?? [];
    const pool = extra ? [...catalog, ...extra] : catalog;
    const wanted = new Set(ids);
    const out: PortfolioSymptom[] = [];
    const seen = new Set<string>();
    for (const s of pool) {
      if (!wanted.has(s.id) || seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ id: s.id, name: s.name, status: s.status });
    }
    return out;
  }

  // Replace the symptom map entry for one strain. A fresh Map reference
  // re-fires the chips + the Symptoms filter memo; the card <li> is keyed
  // off the stock list (not this map) so the editor stays mounted.
  function writeStrainSymptoms(strainId: string, next: PortfolioSymptom[]) {
    mutateStrainSymptoms((prev) => {
      const m = new Map(prev ?? []);
      if (next.length) m.set(strainId, next);
      else m.delete(strainId);
      return m;
    });
  }

  async function applyStrainSymptoms(strainId: string, symptomIds: string[]) {
    await setStrainSymptoms(strainId, symptomIds);
    writeStrainSymptoms(strainId, symptomsFromIds(symptomIds));
  }

  async function suggestStrainSymptom(
    strainId: string,
    name: string,
    currentIds: string[],
  ): Promise<Symptom> {
    const created = await suggestSymptom(name);
    const nextIds = currentIds.includes(created.id)
      ? currentIds
      : [...currentIds, created.id];
    if (nextIds !== currentIds) await setStrainSymptoms(strainId, nextIds);
    mutateCatalog((prev) => {
      if (!prev) return [created];
      if (prev.some((s) => s.id === created.id)) return prev;
      return [...prev, created];
    });
    writeStrainSymptoms(strainId, symptomsFromIds(nextIds, [created]));
    return created;
  }

  // URL-backed filter state. Note `inStockOnly` defaults to true here
  // (vs false on /strains) — urlBool's third argument captures that
  // so the URL only carries `?instock=0` when the user has opted out.
  const { params, set: setParams } = urlFilters();
  const [search, setSearch] = urlString(params, setParams, 'q');
  const [pharmacyParam, setPharmacyParam] = urlString(params, setParams, 'pharm');
  // Existing call sites use `string | null`; bridge through the
  // empty-string convention from urlString.
  const pharmacyFilter = (): string | null => pharmacyParam() || null;
  const setPharmacyFilter = (v: string | null) => setPharmacyParam(v ?? '');
  const [inStockOnly, setInStockOnly] = urlBool(params, setParams, 'instock', true);
  const [maxPrice, setMaxPrice] = urlNumber(params, setParams, 'maxprice');
  const [minThc, setMinThc] = urlNumber(params, setParams, 'thcmin');
  const [maxThc, setMaxThc] = urlNumber(params, setParams, 'thcmax');
  const [minCbd, setMinCbd] = urlNumber(params, setParams, 'cbdmin');
  const [maxCbd, setMaxCbd] = urlNumber(params, setParams, 'cbdmax');
  const [lineages, setLineages] = urlSet<LineageFilter>(
    params,
    setParams,
    'lin',
    isLineageFilter,
  );
  const [categories, setCategories] = urlSet<Category>(
    params,
    setParams,
    'cat',
    isCategory,
  );
  const [irradiations, setIrradiations] = urlSet<IrradiationFilter>(
    params,
    setParams,
    'irr',
    isIrradiationFilter,
  );
  const [brands, setBrands] = urlStringSet(params, setParams, 'brand');
  // Symptom filter is over the viewer's OWN tags, so it isn't really
  // shareable, but we keep it on the URL for consistency with the other
  // filters and so a reload preserves it.
  const [symptomFilter, setSymptomFilter] = urlStringSet(params, setParams, 'sym');
  function toggleSymptom(v: string) {
    const next = new Set(symptomFilter());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setSymptomFilter(next);
  }
  const availableSymptoms = createMemo(() => {
    const set = new Set<string>();
    for (const list of (strainSymptoms() ?? new Map()).values())
      for (const s of list as PortfolioSymptom[]) set.add(s.name);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  });

  // brandQuery and openOverrides stay local — see StrainListRoute for
  // the same reasoning. They're UI affordances, not part of the
  // shareable filter state.
  const [brandQuery, setBrandQuery] = createSignal('');
  // Explicit per-category open/close choices. A category that isn't in
  // the map falls back to the auto-expand default (see sectionOpen):
  // when the active filter narrows results to 1–2 categories we open
  // them by default so the patient doesn't have to click into a near-
  // empty accordion.
  const [openOverrides, setOpenOverrides] = createSignal<Map<Category, boolean>>(new Map());
  function toggleCategoryOpen(c: Category, open: boolean) {
    const next = new Map(openOverrides());
    next.set(c, open);
    setOpenOverrides(next);
  }

  // Master predicate — one definition, reused for the results list AND for
  // each filter's "available options" derivation. `exclude` lets a filter
  // skip its own constraint so its option list reflects what would be
  // possible if that filter were open.
  function matchesFilters(e: LiveStockEntry, exclude?: FilterKey): boolean {
    if (exclude !== 'inStock' && inStockOnly() && e.status !== 'In Stock' && e.status !== 'Low Stock')
      return false;
    if (exclude !== 'pharmacy' && pharmacyFilter() && e.pharmacy_slug !== pharmacyFilter())
      return false;
    if (exclude !== 'search') {
      const q = search().trim().toLowerCase();
      if (q) {
        const hay = `${e.strain.brand} ${e.strain.name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
    }
    if (exclude !== 'price' && maxPrice() != null) {
      if (e.price_per_gram_gbp == null || e.price_per_gram_gbp > maxPrice()!) return false;
    }
    if (exclude !== 'thc' && (minThc() != null || maxThc() != null)) {
      if (e.thc_pct_advertised == null) return false;
      if (minThc() != null && e.thc_pct_advertised < minThc()!) return false;
      if (maxThc() != null && e.thc_pct_advertised > maxThc()!) return false;
    }
    if (exclude !== 'cbd' && (minCbd() != null || maxCbd() != null)) {
      if (e.cbd_pct_advertised == null) return false;
      if (minCbd() != null && e.cbd_pct_advertised < minCbd()!) return false;
      if (maxCbd() != null && e.cbd_pct_advertised > maxCbd()!) return false;
    }
    if (exclude !== 'category' && categories().size > 0) {
      if (!categories().has(e.strain.category as Category)) return false;
    }
    if (exclude !== 'lineage' && lineages().size > 0) {
      const li = lineages();
      if (e.strain.lineage == null) {
        if (!li.has('unknown')) return false;
      } else {
        if (!li.has(e.strain.lineage as Lineage)) return false;
      }
    }
    if (exclude !== 'irradiation' && irradiations().size > 0) {
      const ir = irradiations();
      if (e.irradiation == null) {
        if (!ir.has('unknown')) return false;
      } else {
        if (!ir.has(e.irradiation)) return false;
      }
    }
    if (exclude !== 'brand' && brands().size > 0 && !brands().has(e.strain.brand)) return false;
    if (exclude !== 'symptom' && symptomFilter().size > 0) {
      // OR semantics over the viewer's own tags for this strain.
      const tags = symptomsFor(e.strain.id).map((s) => s.name);
      if (!tags.some((t) => symptomFilter().has(t))) return false;
    }
    return true;
  }

  // Toggle helpers
  function toggleLineage(v: LineageFilter) {
    const next = new Set(lineages());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setLineages(next);
  }
  function toggleCategory(v: Category) {
    const next = new Set(categories());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setCategories(next);
  }
  function toggleIrradiation(v: IrradiationFilter) {
    const next = new Set(irradiations());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setIrradiations(next);
  }
  function toggleBrand(v: string) {
    const next = new Set(brands());
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setBrands(next);
  }

  function resetAll() {
    setSearch('');
    setPharmacyFilter(null);
    setInStockOnly(true);
    setMaxPrice(null);
    setMinThc(null);
    setMaxThc(null);
    setMinCbd(null);
    setMaxCbd(null);
    setLineages(new Set<LineageFilter>());
    setCategories(new Set<Category>());
    setIrradiations(new Set<IrradiationFilter>());
    setBrands(new Set<string>());
    setBrandQuery('');
    setSymptomFilter(new Set<string>());
  }

  // Option subsets — each computed from entries after applying all OTHER
  // filters, so selecting Flower narrows the Brand list to brands that
  // actually have a Flower row, etc.
  const subsetForPharmacy = createMemo(() =>
    (entries() ?? []).filter((e) => matchesFilters(e, 'pharmacy')),
  );
  const subsetForCategory = createMemo(() =>
    (entries() ?? []).filter((e) => matchesFilters(e, 'category')),
  );
  const subsetForLineage = createMemo(() =>
    (entries() ?? []).filter((e) => matchesFilters(e, 'lineage')),
  );
  const subsetForIrradiation = createMemo(() =>
    (entries() ?? []).filter((e) => matchesFilters(e, 'irradiation')),
  );
  const subsetForBrand = createMemo(() =>
    (entries() ?? []).filter((e) => matchesFilters(e, 'brand')),
  );

  const availablePharmacies = createMemo(() => {
    const map = new Map<string, string>();
    for (const e of subsetForPharmacy()) map.set(e.pharmacy_slug, e.pharmacy_name);
    // Preserve current selection even if other filters eliminated it —
    // otherwise users can't un-select a stale choice.
    if (pharmacyFilter()) {
      const current = (entries() ?? []).find((e) => e.pharmacy_slug === pharmacyFilter());
      if (current) map.set(current.pharmacy_slug, current.pharmacy_name);
    }
    return Array.from(map.entries())
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  const availableCategories = createMemo(() => {
    const set = new Set<Category>();
    for (const e of subsetForCategory()) set.add(e.strain.category as Category);
    for (const c of categories()) set.add(c);
    return CATEGORY_OPTIONS.filter((c) => set.has(c));
  });

  const availableLineages = createMemo(() => {
    const set = new Set<LineageFilter>();
    for (const e of subsetForLineage()) {
      set.add((e.strain.lineage ?? 'unknown') as LineageFilter);
    }
    for (const l of lineages()) set.add(l);
    return LINEAGE_OPTIONS.filter((l) => set.has(l));
  });

  const availableIrradiations = createMemo(() => {
    const set = new Set<IrradiationFilter>();
    for (const e of subsetForIrradiation()) set.add((e.irradiation ?? 'unknown') as IrradiationFilter);
    for (const i of irradiations()) set.add(i);
    return IRRADIATION_FILTER_OPTIONS.filter((i) => set.has(i));
  });

  const availableBrands = createMemo(() => {
    const set = new Set<string>();
    for (const e of subsetForBrand()) set.add(e.strain.brand);
    for (const b of brands()) set.add(b);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  });

  const filteredBrandOptions = createMemo(() => {
    const q = brandQuery().toLowerCase().trim();
    const all = availableBrands();
    if (!q) return all;
    return all.filter((b) => b.toLowerCase().includes(q));
  });

  const observedMaxPrice = createMemo(() => {
    let max = 0;
    for (const e of entries() ?? []) {
      if (e.price_per_gram_gbp != null && e.price_per_gram_gbp > max) max = e.price_per_gram_gbp;
    }
    return Math.ceil(max) || DEFAULT_PRICE_MAX;
  });

  const observedMaxThc = createMemo(() => {
    let max = 0;
    for (const e of entries() ?? []) {
      if (e.thc_pct_advertised != null && e.thc_pct_advertised > max) max = e.thc_pct_advertised;
    }
    return Math.ceil(max) || DEFAULT_THC_MAX;
  });

  const observedMaxCbd = createMemo(() => {
    let max = 0;
    for (const e of entries() ?? []) {
      if (e.cbd_pct_advertised != null && e.cbd_pct_advertised > max) max = e.cbd_pct_advertised;
    }
    return Math.ceil(max) || DEFAULT_CBD_MAX;
  });

  const filtered = createMemo(() => (entries() ?? []).filter((e) => matchesFilters(e)));

  // Drop excluded-brand rows unless the user opts to show them.
  const visibleEntries = createMemo(() => {
    const ex = excludedBrands();
    const base = filtered();
    if (showExcluded() || !ex || ex.size === 0) return base;
    return base.filter((e) => !ex.has(e.strain.brand));
  });
  const excludedInResults = createMemo(() => {
    const ex = excludedBrands();
    if (!ex || ex.size === 0) return 0;
    const brands = new Set(
      filtered().filter((e) => ex.has(e.strain.brand)).map((e) => e.strain.id),
    );
    return brands.size;
  });

  // One card per strain. Since potency is part of strain identity now
  // (migration 0048), a strain_id is a single product at one potency, so
  // summariseStock rolls its pharmacy rows up into a single summary
  // (best status + cheapest price + union of irradiation). Per-pharmacy
  // detail still lives on the Strain Detail page.
  type StockCard = {
    key: string;
    strain: LiveStockEntry['strain'];
    summary: StockSummary;
  };

  const stockCards = createMemo<StockCard[]>(() => {
    const byStrain = new Map<string, LiveStockEntry[]>();
    for (const e of visibleEntries()) {
      const list = byStrain.get(e.strain.id) ?? [];
      list.push(e);
      byStrain.set(e.strain.id, list);
    }
    const cards: StockCard[] = [];
    for (const rows of byStrain.values()) {
      const strain = rows[0]!.strain;
      const summary = summariseStock(rows);
      if (summary) cards.push({ key: strain.id, strain, summary });
    }
    return cards.sort((a, b) => a.strain.name.localeCompare(b.strain.name));
  });

  const groupedByCategory = createMemo(() => {
    const buckets = new Map<Category, StockCard[]>();
    for (const c of stockCards()) {
      const cat = c.strain.category as Category;
      const list = buckets.get(cat) ?? [];
      list.push(c);
      buckets.set(cat, list);
    }
    return CATEGORY_OPTIONS
      .filter((c) => (buckets.get(c)?.length ?? 0) > 0)
      .map((c) => ({
        category: c,
        rows: buckets.get(c) ?? [],
      }));
  });

  // Auto-expand: when the filter narrows to 1–2 category sections, open
  // them by default. An explicit user toggle (openOverrides) always wins.
  const autoExpand = createMemo(() => groupedByCategory().length <= 2);
  function sectionOpen(c: Category): boolean {
    const explicit = openOverrides().get(c);
    return explicit !== undefined ? explicit : autoExpand();
  }

  const activeFilterCount = createMemo(() => {
    let n = 0;
    if (search()) n++;
    if (pharmacyFilter()) n++;
    if (!inStockOnly()) n++;
    if (maxPrice() != null) n++;
    if (minThc() != null || maxThc() != null) n++;
    if (minCbd() != null || maxCbd() != null) n++;
    if (lineages().size) n++;
    if (categories().size) n++;
    if (irradiations().size) n++;
    if (brands().size) n++;
    if (symptomFilter().size) n++;
    return n;
  });

  return (
    <section class="px-4 sm:px-6 py-10 max-w-7xl mx-auto grid lg:grid-cols-[280px_1fr] gap-8">
      <PageHead
        title="Live Stock · Terp Track"
        description="Live UK medical cannabis stock across every tracked pharmacy. Filter by THC, CBD, irradiation, and brand to find what's in stock right now."
        canonical="/stock"
      />
      <FilterDrawer activeCount={activeFilterCount}>
        <div class="flex items-center justify-between">
          <h2 class="font-display text-lg font-bold">Filters</h2>
          <Show when={activeFilterCount() > 0}>
            <button
              type="button"
              onClick={resetAll}
              class="text-xs text-brand-moss hover:underline"
            >
              Reset all
            </button>
          </Show>
        </div>

        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Search</span>
          <input
            type="search" name="search"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            placeholder="Strain or brand…"
            class="rounded-md border border-brand-hex px-3 py-2 text-sm"
          />
        </label>

        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={inStockOnly()}
            onChange={(e) => setInStockOnly(e.currentTarget.checked)}
            name="filter" class="accent-brand-moss"
          />
          <span class="font-medium">In stock only</span>
        </label>

        <fieldset>
          <legend class="text-sm font-medium mb-1">Type</legend>
          <For
            each={availableCategories()}
            fallback={<p class="text-xs text-brand-green/50">No options match other filters.</p>}
          >
            {(c) => (
              <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={categories().has(c)}
                  onChange={() => toggleCategory(c)}
                  name="filter" class="accent-brand-moss"
                />
                {c}
              </label>
            )}
          </For>
        </fieldset>

        <fieldset>
          <legend class="text-sm font-medium mb-2">Pharmacy</legend>
          <div class="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setPharmacyFilter(null)}
              class={`rounded-full px-2.5 py-1 text-xs ${
                pharmacyFilter() === null
                  ? 'bg-brand-green text-brand-cream'
                  : 'bg-brand-hex/60 text-brand-green'
              }`}
            >
              All
            </button>
            <For each={availablePharmacies()}>
              {(p) => (
                <button
                  type="button"
                  onClick={() => setPharmacyFilter(p.slug)}
                  class={`rounded-full px-2.5 py-1 text-xs ${
                    pharmacyFilter() === p.slug
                      ? 'bg-brand-green text-brand-cream'
                      : 'bg-brand-hex/60 text-brand-green'
                  }`}
                >
                  {p.name}
                </button>
              )}
            </For>
          </div>
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">Max price £/g</legend>
            <Show when={maxPrice() != null}>
              <button
                type="button"
                onClick={() => setMaxPrice(null)}
                class="text-xs text-brand-moss hover:underline"
              >
                any
              </button>
            </Show>
          </div>
          <div class="flex items-center gap-2 text-sm">
            <input
              type="range" name="range"
              min="1"
              max={observedMaxPrice()}
              step="0.5"
              value={maxPrice() ?? observedMaxPrice()}
              onInput={(e) => setMaxPrice(Number(e.currentTarget.value))}
              class="flex-1 accent-brand-moss"
            />
            <span class="font-display font-semibold w-16 text-right">
              £{(maxPrice() ?? observedMaxPrice()).toFixed(2)}
            </span>
          </div>
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">THC %</legend>
            <Show when={minThc() != null || maxThc() != null}>
              <button
                type="button"
                onClick={() => {
                  setMinThc(null);
                  setMaxThc(null);
                }}
                class="text-xs text-brand-moss hover:underline"
              >
                any
              </button>
            </Show>
          </div>
          <DualRangeSlider
            min={0}
            max={observedMaxThc()}
            step={1}
            value={{ min: minThc() ?? 0, max: maxThc() ?? observedMaxThc() }}
            onInput={(v) => {
              setMinThc(v.min);
              setMaxThc(v.max);
            }}
            format={(v) => `${v}%`}
          />
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">CBD %</legend>
            <Show when={minCbd() != null || maxCbd() != null}>
              <button
                type="button"
                onClick={() => {
                  setMinCbd(null);
                  setMaxCbd(null);
                }}
                class="text-xs text-brand-moss hover:underline"
              >
                any
              </button>
            </Show>
          </div>
          <DualRangeSlider
            min={0}
            max={observedMaxCbd()}
            step={0.1}
            value={{ min: minCbd() ?? 0, max: maxCbd() ?? observedMaxCbd() }}
            onInput={(v) => {
              setMinCbd(v.min);
              setMaxCbd(v.max);
            }}
            format={(v) => `${v.toFixed(1)}%`}
          />
        </fieldset>

        <fieldset>
          <legend class="text-sm font-medium mb-1">Profile</legend>
          <For
            each={availableLineages()}
            fallback={<p class="text-xs text-brand-green/50">No options match other filters.</p>}
          >
            {(l) => (
              <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={lineages().has(l)}
                  onChange={() => toggleLineage(l)}
                  name="filter" class="accent-brand-moss"
                />
                {LINEAGE_LABELS[l]}
              </label>
            )}
          </For>
        </fieldset>

        <fieldset>
          <legend class="text-sm font-medium mb-1">Irradiation</legend>
          <For
            each={availableIrradiations()}
            fallback={<p class="text-xs text-brand-green/50">No options match other filters.</p>}
          >
            {(k) => (
              <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={irradiations().has(k)}
                  onChange={() => toggleIrradiation(k)}
                  name="filter" class="accent-brand-moss"
                />
                {IRRADIATION_FILTER_LABELS[k]}
              </label>
            )}
          </For>
        </fieldset>

        <fieldset>
          <div class="flex items-center justify-between mb-1">
            <legend class="text-sm font-medium">Brand</legend>
            <Show when={brands().size > 0}>
              <button
                type="button"
                onClick={() => setBrands(new Set<string>())}
                class="text-xs text-brand-moss hover:underline"
              >
                clear
              </button>
            </Show>
          </div>
          <input
            type="search" name="search"
            value={brandQuery()}
            onInput={(e) => setBrandQuery(e.currentTarget.value)}
            placeholder="Search brand…"
            class="w-full rounded-md border border-brand-hex px-2 py-1 text-xs mb-1"
          />
          <div class="max-h-44 overflow-y-auto pr-1">
            <For
              each={filteredBrandOptions()}
              fallback={<p class="text-xs text-brand-green/50 py-1">No brands match other filters.</p>}
            >
              {(b) => (
                <label class="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={brands().has(b)}
                    onChange={() => toggleBrand(b)}
                    name="filter" class="accent-brand-moss"
                  />
                  <span class="truncate">{b}</span>
                </label>
              )}
            </For>
          </div>
        </fieldset>

        <Show when={availableSymptoms().length > 0}>
          <fieldset>
            <div class="flex items-center justify-between mb-1">
              <legend class="text-sm font-medium">Symptoms</legend>
              <Show when={symptomFilter().size > 0}>
                <button
                  type="button"
                  onClick={() => setSymptomFilter(new Set<string>())}
                  class="text-xs text-brand-moss hover:underline"
                >
                  clear
                </button>
              </Show>
            </div>
            <For each={availableSymptoms()}>
              {(s) => (
                <label class="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={symptomFilter().has(s)}
                    onChange={() => toggleSymptom(s)}
                    name="filter" class="accent-brand-moss"
                  />
                  {s}
                </label>
              )}
            </For>
          </fieldset>
        </Show>
      </FilterDrawer>

      <div>
        <div class="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 class="font-display text-2xl sm:text-3xl font-bold">Live Stock</h1>
            <p class="text-sm text-brand-green/80 mt-1">
              {stockCards().length} listing{stockCards().length === 1 ? '' : 's'}
              {activeFilterCount() > 0 &&
                ` · ${activeFilterCount()} filter${activeFilterCount() === 1 ? '' : 's'} active`}
            </p>
          </div>
        </div>

        <Show when={entries.error}>
          <p class="mt-6 text-sm text-red-700" role="alert">
            Failed to load stock: {(entries.error as Error).message}
          </p>
        </Show>

        <Show when={excludedInResults() > 0}>
          <div class="mt-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <span>
              {excludedInResults()} strain{excludedInResults() === 1 ? '' : 's'} from brands you've
              hidden {showExcluded() ? 'are shown below' : 'are hidden'}.
            </span>
            <button
              type="button"
              onClick={() => setShowExcluded(!showExcluded())}
              class="shrink-0 rounded-full border border-amber-400 px-3 py-1 font-medium cursor-pointer hover:bg-amber-100 transition-colors"
            >
              {showExcluded() ? 'Hide them' : 'Show anyway'}
            </button>
          </div>
        </Show>

        <Show
          when={!entries.loading}
          fallback={<p class="mt-8 text-sm">Loading live stock…</p>}
        >
          <div class="mt-6">
          <MedicalStatusGate
            message="Access to the Live Stock is only available for UK Medical Cannabis patients."
            when={() => (
          <Show
            when={groupedByCategory().length > 0}
            fallback={
              <p class="mt-8 text-sm text-brand-green/80">
                No matching strains. Try relaxing a filter.
              </p>
            }
          >
            <div class="space-y-3">
              <For each={groupedByCategory()}>
                {(section) => (
                  <CollapsibleSection
                    title={section.category}
                    count={section.rows.length}
                    open={sectionOpen(section.category)}
                    onToggle={(o) => toggleCategoryOpen(section.category, o)}
                  >
                    <ul class="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                      <For each={section.rows}>
                        {(card) => (
                          <li class="rounded-xl border border-brand-hex bg-white p-3 flex flex-col gap-1">
                            <div class="flex items-start justify-between gap-2">
                              <div class="min-w-0">
                                <p class="text-xs text-brand-green/80 uppercase tracking-wide truncate">
                                  {card.strain.brand}
                                </p>
                                <A
                                  href={`/strains/${card.strain.slug}`}
                                  class="font-display font-semibold hover:underline block truncate"
                                >
                                  {strainNameWithThc(card.strain.name, card.summary.thc)}
                                </A>
                              </div>
                              <StockBadge status={card.summary.status} />
                            </div>
                            <div class="flex items-center justify-between text-xs text-brand-green/80">
                              <span>
                                {card.summary.thc != null &&
                                  `THC ${formatPct(card.summary.thc, card.summary.thcUpper)}`}
                                {card.summary.thc != null &&
                                  card.summary.cbd != null &&
                                  ' · '}
                                {card.summary.cbd != null &&
                                  `CBD ${formatPct(card.summary.cbd, card.summary.cbdUpper)}`}
                              </span>
                              <Show when={card.summary.minPrice != null}>
                                <span class="font-medium">
                                  {card.summary.hasPriceVariation && 'from '}£{card.summary.minPrice?.toFixed(2)}/g
                                </span>
                              </Show>
                            </div>
                            <div class="flex items-center gap-1 flex-wrap mt-0.5">
                              <Show when={card.strain.lineage}>
                                <span class="text-[10px] px-2 py-0.5 rounded-full bg-brand-hex text-brand-green capitalize">
                                  {card.strain.lineage}
                                </span>
                              </Show>
                              <For each={card.summary.irradiations}>
                                {(k) => (
                                  <span class="text-[10px] px-2 py-0.5 rounded-full bg-brand-moss/10 text-brand-green">
                                    {IRRADIATION_LABELS[k]}
                                  </span>
                                )}
                              </For>
                              <Show when={card.summary.pharmacyCount > 1}>
                                <span class="text-[10px] px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green">
                                  at {card.summary.pharmacyCount} pharmacies
                                </span>
                              </Show>
                            </div>
                            <CardQuickActions
                              strainId={card.strain.id}
                              initialInPortfolio={portfolioIds()?.has(card.strain.id) ?? false}
                              initialInWatchlist={watchedIds()?.has(card.strain.id) ?? false}
                            />
                            <Show when={authStore.user()}>
                              <RelationalSymptomEditor
                                current={symptomsFor(card.strain.id)}
                                catalog={symptomCatalog() ?? []}
                                onChange={(ids) => applyStrainSymptoms(card.strain.id, ids)}
                                onSuggest={(name, currentIds) =>
                                  suggestStrainSymptom(card.strain.id, name, currentIds)
                                }
                              />
                            </Show>
                          </li>
                        )}
                      </For>
                    </ul>
                  </CollapsibleSection>
                )}
              </For>
            </div>
          </Show>
            )}
          />
          </div>
        </Show>
      </div>
    </section>
  );
}
